import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FileUp, MapPin, Package, Send, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";

function B2BRFQ() {
  const navigate = useNavigate();
  const brand = useMemo(
    () => ({
      bg: "#F7FAFC",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
      accent: "#0EA5E9",
      accent2: "#22C55E",
    }),
    []
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1400&h=360&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
              Request for Quote (RFQ)
            </Typography>
            <Typography sx={{ color: brand.subtext, mt: 0.5, maxWidth: 840 }}>
              Upload a requirement list or enter items manually. Suppliers will respond with
              price breaks, lead time, and delivery terms.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
              <Chip icon={<Package size={16} />} label="MOQ & bulk pricing" />
              <Chip icon={<Truck size={16} />} label="Lead time & delivery" />
              <Chip icon={<MapPin size={16} />} label="Ship-to location" />
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>
                  RFQ details
                </Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mb: 2 }}>
                  Fill basic details. (Demo UI — connect to API later.)
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="RFQ Title"
                      placeholder="e.g., Packaging supplies for April"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Delivery city" placeholder="Hyderabad" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Needed by" placeholder="yyyy-mm-dd" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      label="Items & quantities"
                      placeholder={"1) Packaging Tape (Case) - Qty 240\n2) Thermal Labels (Roll) - Qty 200\n3) Boxes - Qty 100"}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      borderColor: brand.stroke,
                      color: brand.text,
                    }}
                    startIcon={<FileUp size={16} />}
                  >
                    Upload requirement file
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/B2BOrders")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      color: "white",
                      background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                    }}
                    startIcon={<Send size={16} />}
                  >
                    Submit RFQ
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>
                  Supplier response preview
                </Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mb: 2 }}>
                  What you’ll typically receive.
                </Typography>

                <Stack spacing={1.25}>
                  {[
                    {
                      title: "Price breaks",
                      desc: "₹78 / case (50+) • ₹74 / case (200+)",
                    },
                    { title: "Lead time", desc: "Same-day dispatch • 2–3 days delivery" },
                    { title: "Payment terms", desc: "Net-30 • GST invoice included" },
                    { title: "Shipping", desc: "Delivered to Hyderabad • insured" },
                  ].map((x) => (
                    <Box
                      key={x.title}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: "white",
                        border: `1px solid ${brand.stroke}`,
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, color: brand.text }}>
                        {x.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: brand.subtext }}>
                        {x.desc}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate("/B2BContracts")}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 950,
                    borderColor: brand.stroke,
                    color: brand.text,
                  }}
                >
                  View contract vendors
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BRFQ;

